import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, type KeyboardEvent } from 'react';

import type { ReportEntry, ReportMetric } from '../../../types/report.js';
import {
  ColumnFilters,
  FILTERABLE_COLUMNS,
  type FilterableColumn,
  encodeFilterValue,
  entryMatchesFilters,
  getLabelForEncodedFilterValue,
  getLabelForFilterValue,
  matchesColumnFilter,
} from '../lib/columnFilters.js';
import { ColumnFilterMenu, type ColumnFilterOption } from './ColumnFilterMenu.js';

const formatNumber = (value: number) => new Intl.NumberFormat('cs-CZ').format(value);

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const averageFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentageFormatter = (value: number) =>
  `${(value * 100).toFixed(1).replace(/\.0$/, '')} %`;

const buildInstallsTooltip = (breakdown: ReportEntry['metrics']['asaInstallsBreakdown']) => {
  if (!breakdown) {
    return '';
  }
  const items: Array<[string, number]> = [
    ['Celkem instalací', breakdown.totalInstalls],
    ['Nové instalace', breakdown.totalNewDownloads],
    ['Přeinstalace', breakdown.totalRedownloads],
    ['Tap instalace', breakdown.tapInstalls],
    ['View instalace', breakdown.viewInstalls],
  ];
  return items
    .filter(([, value], index) => value > 0 || index === 0)
    .map(([label, value]) => `${label}: ${formatNumber(value)}`)
    .join('\n');
};

type SortableField =
  | 'campaignName'
  | 'adGroupName'
  | 'usersCount'
  | 'totalValue'
  | 'averageValue'
  | 'cost'
  | 'cpi'
  | 'roi';

export interface ReportTableProps {
  entries: ReportEntry[];
  metric: ReportMetric;
  sort: { by: SortableField; order: 'asc' | 'desc' };
  onSortChange: (next: { by: SortableField; order: 'asc' | 'desc' }) => void;
  selectedTarget?: {
    campaignName: string;
    campaignId: string | null;
    adGroupName: string | null;
    adGroupId: string | null;
    keywordName: string;
    keywordId: string | null;
    orgId: string | null;
  } | null;
  onSelectEntry?: (entry: ReportEntry) => void;
  columnFilters: ColumnFilters;
  onColumnFiltersChange: (column: FilterableColumn, nextValues: string[]) => void;
}

const getNextOrder = (currentOrder: 'asc' | 'desc') => (currentOrder === 'desc' ? 'asc' : 'desc');

export const ReportTable = ({
  entries,
  metric,
  sort,
  onSortChange,
  selectedTarget,
  onSelectEntry,
  columnFilters,
  onColumnFiltersChange,
}: ReportTableProps) => {
  const advancedEntries = entries.filter((entry) => entry.group.appleAdsType !== 'BASIC');
  const basicEntries = entries.filter((entry) => entry.group.appleAdsType === 'BASIC');

  if (advancedEntries.length === 0 && basicEntries.length === 0) {
    return (
      <Typography color="text.secondary" align="center">
        Žádná data pro zvolené období.
      </Typography>
    );
  }

  const filteredAdvancedEntries = useMemo(
    () => advancedEntries.filter((entry) => entryMatchesFilters(entry, columnFilters)),
    [advancedEntries, columnFilters]
  );

  const filterOptions = useMemo(
    () => buildFilterOptions(advancedEntries, columnFilters),
    [advancedEntries, columnFilters]
  );

  const totals = useMemo(
    () =>
      filteredAdvancedEntries.reduce(
        (acc, entry) => {
          acc.users += entry.metrics.usersCount;
          acc.backend += entry.metrics.backendUsersCount;
          acc.total += entry.metrics.totalValue;
          acc.cost += entry.metrics.cost;
          const breakdown = entry.metrics.asaInstallsBreakdown;
          if (breakdown) {
            acc.breakdown.totalInstalls += breakdown.totalInstalls;
            acc.breakdown.totalNewDownloads += breakdown.totalNewDownloads;
            acc.breakdown.totalRedownloads += breakdown.totalRedownloads;
            acc.breakdown.tapInstalls += breakdown.tapInstalls;
            acc.breakdown.viewInstalls += breakdown.viewInstalls;
            acc.breakdown.tapNewDownloads += breakdown.tapNewDownloads;
            acc.breakdown.tapRedownloads += breakdown.tapRedownloads;
            acc.breakdown.viewNewDownloads += breakdown.viewNewDownloads;
            acc.breakdown.viewRedownloads += breakdown.viewRedownloads;
          }
          return acc;
        },
        {
          users: 0,
          backend: 0,
          total: 0,
          cost: 0,
          breakdown: {
            totalInstalls: 0,
            tapInstalls: 0,
            totalNewDownloads: 0,
            totalRedownloads: 0,
            viewInstalls: 0,
            tapNewDownloads: 0,
            tapRedownloads: 0,
            viewNewDownloads: 0,
            viewRedownloads: 0,
          },
        }
      ),
    [filteredAdvancedEntries]
  );

  const showRoi = metric === 'revenue';
  const averageValue = totals.users > 0 ? totals.total / totals.users : 0;
  const roiValue = totals.cost > 0 ? totals.total / totals.cost : null;
  const summaryCpi = totals.users > 0 ? totals.cost / totals.users : null;
  const advancedColumnCount = showRoi ? 9 : 8;

  const handleSort = (field: SortableField) => {
    if (sort.by === field) {
      onSortChange({ by: field, order: getNextOrder(sort.order) });
    } else {
      onSortChange({ by: field, order: 'desc' });
    }
  };

  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, entry: ReportEntry) => {
    if (!onSelectEntry) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectEntry(entry);
    }
  };

  const renderAdvancedRow = (entry: ReportEntry) => {
    const selectable =
      Boolean(entry.group.campaignName && entry.group.keywordName && entry.identifiers.keywordId) &&
      Boolean(onSelectEntry);

    const isSelected =
      selectable &&
      !!selectedTarget &&
      (selectedTarget.keywordId
        ? selectedTarget.keywordId === (entry.identifiers.keywordId ?? null) &&
          selectedTarget.campaignId === (entry.identifiers.campaignId ?? null) &&
          selectedTarget.adGroupId === (entry.identifiers.adGroupId ?? null)
        : selectedTarget.campaignName === entry.group.campaignName &&
          selectedTarget.keywordName === entry.group.keywordName &&
          selectedTarget.adGroupName === (entry.group.adGroupName ?? null));

    const installsTooltip = buildInstallsTooltip(entry.metrics.asaInstallsBreakdown);
    const cpiValue =
      entry.metrics.usersCount > 0
        ? currencyFormatter.format(entry.metrics.cost / entry.metrics.usersCount)
        : null;

  return (
    <TableRow
        key={`${entry.identifiers.campaignId}-${entry.identifiers.keywordId}`}
        hover={selectable}
        selected={Boolean(isSelected)}
        onClick={() => (selectable ? onSelectEntry?.(entry) : undefined)}
        onKeyDown={(event) => (selectable ? handleRowKeyDown(event, entry) : undefined)}
        role={selectable ? 'button' : undefined}
        tabIndex={selectable ? 0 : undefined}
        sx={{ cursor: selectable ? 'pointer' : 'default' }}
      >
        <TableCell sx={{ maxWidth: 260 }}>
          <Typography fontWeight={600}>{entry.group.campaignName ?? '—'}</Typography>
        </TableCell>
        <TableCell>{entry.group.adGroupName ?? '—'}</TableCell>
        <TableCell>{entry.group.keywordName ?? '—'}</TableCell>
        <TableCell>
          <Tooltip title={installsTooltip}>
            <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(entry.metrics.usersCount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({formatNumber(entry.metrics.backendUsersCount)})
              </Typography>
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{currencyFormatter.format(entry.metrics.cost)}</TableCell>
        <TableCell align="right">{cpiValue ?? '—'}</TableCell>
        <TableCell align="right">
          {metric === 'revenue'
            ? currencyFormatter.format(entry.metrics.totalValue)
            : formatNumber(entry.metrics.totalValue)}
        </TableCell>
        <TableCell align="right">
          {metric === 'revenue'
            ? averageFormatter.format(entry.metrics.averageValue)
            : percentageFormatter(entry.metrics.averageValue)}
        </TableCell>
        {showRoi ? (
          <TableCell align="right">
            {entry.metrics.roi === null ? '—' : percentageFormatter(entry.metrics.roi)}
          </TableCell>
        ) : null}
      </TableRow>
    );
  };

  const renderSortCell = (field: SortableField, label: string) => (
    <TableCell sortDirection={sort.by === field ? sort.order : false}>
      <TableSortLabel
        active={sort.by === field}
        direction={sort.by === field ? sort.order : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const renderFilterableHeaderCell = (
    column: FilterableColumn,
    label: string,
    sortableField?: SortableField
  ) => {
    const sortProps =
      sortableField !== undefined
        ? ({ sortDirection: sort.by === sortableField ? sort.order : undefined } as const)
        : undefined;
    const labelNode =
      sortableField !== undefined ? (
        <TableSortLabel
          active={sort.by === sortableField}
          direction={sort.by === sortableField ? sort.order : 'asc'}
          onClick={() => handleSort(sortableField)}
        >
          {label}
        </TableSortLabel>
      ) : (
        <Typography component="span" fontWeight={600}>
          {label}
        </Typography>
      );

    return (
      <TableCell sortDirection={sortProps?.sortDirection}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {labelNode}
          <ColumnFilterMenu
            label={label}
            options={filterOptions[column]}
            selectedValues={columnFilters[column]}
            onChange={(nextValues) => onColumnFiltersChange(column, nextValues)}
          />
        </Box>
      </TableCell>
    );
  };

  return (
    <>
      {advancedEntries.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {renderFilterableHeaderCell('campaignName', 'Kampaň', 'campaignName')}
                {renderFilterableHeaderCell('adGroupName', 'Ad group', 'adGroupName')}
                {renderFilterableHeaderCell('keywordName', 'Keyword')}
                {renderSortCell('usersCount', 'Instalace (ASA)')}
                {renderSortCell('cost', 'Spend')}
                {renderSortCell('cpi', 'CPI')}
                {renderSortCell('totalValue', metric === 'revenue' ? 'Revenue' : 'Počet trialů')}
                {renderSortCell('averageValue', metric === 'revenue' ? 'ARPU' : 'Podíl trialů')}
                {showRoi ? renderSortCell('roi', 'ROAS') : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAdvancedEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={advancedColumnCount} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      Žádné výsledky pro zvolené filtry.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAdvancedEntries.map(renderAdvancedRow)
              )}
            </TableBody>
            <TableFooter>
              <TableRow
                sx={{
                  bgcolor: 'grey.100',
                  '& td': {
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderTop: '2px solid',
                    borderColor: 'text.primary',
                  },
                }}
              >
                <TableCell>Celkem</TableCell>
                <TableCell />
                <TableCell />
                <TableCell>
                  <Tooltip title={buildInstallsTooltip(totals.breakdown)}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(totals.users)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({formatNumber(totals.backend)})
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{currencyFormatter.format(totals.cost)}</TableCell>
                <TableCell align="right">
                  {summaryCpi === null ? '—' : currencyFormatter.format(summaryCpi)}
                </TableCell>
                <TableCell align="right">
                  {metric === 'revenue'
                    ? currencyFormatter.format(totals.total)
                    : formatNumber(totals.total)}
                </TableCell>
                <TableCell align="right">
                  {metric === 'revenue' ? averageFormatter.format(averageValue) : percentageFormatter(averageValue)}
                </TableCell>
                {showRoi ? (
                  <TableCell align="right">
                    {roiValue === null ? '—' : percentageFormatter(roiValue)}
                  </TableCell>
                ) : null}
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      ) : null}

      {basicEntries.length > 0 ? (
        <Box component="section" aria-label="Basic kampaně" mt={4}>
          <Typography variant="h6" gutterBottom>
            Basic kampaně
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Basic kampaně zobrazujeme zvlášť, protože pro ně nejsou dostupné všechny metriky.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
         <TableHead>
                <TableRow>
                  <TableCell>Kampaň</TableCell>
                  <TableCell align="right">Instalace (ASA)</TableCell>
                  <TableCell align="right">
                    {metric === 'revenue' ? 'Revenue' : 'Počet trialů'}
                  </TableCell>
                  <TableCell align="right">
                    {metric === 'revenue' ? 'ARPU' : 'Podíl trialů'}
                  </TableCell>
                </TableRow>
         </TableHead>
         <TableBody>
                {basicEntries.map((entry) => {
                  const users = entry.metrics.backendUsersCount;
                  const average =
                    metric === 'revenue' && users > 0
                      ? entry.metrics.totalValue / users
                      : entry.metrics.averageValue;
                  return (
                    <TableRow key={`${entry.identifiers.campaignId}-${entry.identifiers.keywordId}`}>
                      <TableCell>
                        {entry.group.campaignName ?? entry.identifiers.campaignId ?? '—'}
                      </TableCell>
                      <TableCell align="right">{formatNumber(users)}</TableCell>
                      <TableCell align="right">
                        {metric === 'revenue'
                          ? currencyFormatter.format(entry.metrics.totalValue)
                          : formatNumber(entry.metrics.totalValue)}
                      </TableCell>
                      <TableCell align="right">
                        {metric === 'revenue' ? averageFormatter.format(average) : percentageFormatter(average)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}
    </>
  );
};

const buildFilterOptions = (
  entries: ReportEntry[],
  filters: ColumnFilters
): Record<FilterableColumn, ColumnFilterOption[]> => {
  const result: Record<FilterableColumn, ColumnFilterOption[]> = {
    campaignName: [],
    adGroupName: [],
    keywordName: [],
  };

  for (const column of FILTERABLE_COLUMNS) {
    const otherColumns = FILTERABLE_COLUMNS.filter((name) => name !== column);
    const counts = new Map<string, { label: string; count: number }>();

    for (const entry of entries) {
      const matchesOthers = otherColumns.every((otherColumn) =>
        matchesColumnFilter(entry.group[otherColumn], filters[otherColumn])
      );

      if (!matchesOthers) {
        continue;
      }

      const encoded = encodeFilterValue(entry.group[column]);
      const label = getLabelForFilterValue(entry.group[column]);
      const current = counts.get(encoded);
      counts.set(encoded, {
        label,
        count: current ? current.count + 1 : 1,
      });
    }

    const columnOptions: ColumnFilterOption[] = Array.from(counts.entries()).map(
      ([value, data]) => ({
        value,
        label: data.label,
        count: data.count,
      })
    );

    for (const value of filters[column]) {
      if (!counts.has(value)) {
        columnOptions.push({
          value,
          label: getLabelForEncodedFilterValue(value),
          count: 0,
        });
      }
    }

    columnOptions.sort((a, b) => a.label.localeCompare(b.label, 'cs', { sensitivity: 'base' }));
    result[column] = columnOptions;
  }

  return result;
};
